import { store } from '../store';
import { setUserStatus, updateUserStatus } from '../store/slices/authSlice';
import { updateUserStatusInList } from '../store/slices/userSlice';
import { API_URL } from '../config/constants';
import { recordCallJoin, recordCallLeave } from '../services/attendanceService';

const HEARTBEAT_KEY = 'workloop.callHeartbeatAt';
const LEAVE_DEBOUNCE_MS = 450;
const STALE_MS = 8000;

let depth = 0;
let leaveTimer = null;
let beatTimer = null;

const applyLocal = (status) => {
  const userId = store.getState().auth?.user?.id;
  store.dispatch(setUserStatus({ status, statusSource: 'CALL' }));
  if (userId) {
    store.dispatch(updateUserStatusInList({
      userId,
      status,
      statusSource: 'CALL',
      onBreak: false,
    }));
  }
};

const persistStatus = (status, { keepalive = false } = {}) => {
  applyLocal(status);
  const token = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null;
  if (keepalive && token) {
    try {
      fetch(`${API_URL}/profile/status`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status, source: 'CALL' }),
        keepalive: true,
      }).catch(() => {});
    } catch {
      // ignore
    }
    return;
  }
  store.dispatch(updateUserStatus({ status, source: 'CALL' }));
};

const startBeat = () => {
  const tick = () => {
    try {
      localStorage.setItem(HEARTBEAT_KEY, String(Date.now()));
    } catch {
      // ignore
    }
  };
  tick();
  if (typeof window === 'undefined') return;
  if (beatTimer) window.clearInterval(beatTimer);
  beatTimer = window.setInterval(tick, 2000);
};

const stopBeat = () => {
  if (beatTimer) {
    window.clearInterval(beatTimer);
    beatTimer = null;
  }
  try {
    localStorage.removeItem(HEARTBEAT_KEY);
  } catch {
    // ignore
  }
};

const onCallSurface = () => {
  if (typeof window === 'undefined') return false;
  const path = window.location.pathname || '';
  const hash = window.location.hash || '';
  if (path.startsWith('/call') || hash.startsWith('#/call')) return true;
  try {
    return Boolean(window.workloopRealm?.isCallWindow?.());
  } catch {
    return false;
  }
};

export const enterCallPresence = (meta = {}) => {
  depth += 1;
  if (leaveTimer) {
    window.clearTimeout(leaveTimer);
    leaveTimer = null;
  }
  startBeat();
  persistStatus('IN_A_CALL');
  if (depth === 1) {
    recordCallJoin({
      title: meta.title,
      channelId: meta.channelId,
      messageId: meta.messageId,
      roomName: meta.roomName,
      provider: meta.provider || 'livekit',
      liveSession: true,
    }).catch(() => {});
  }
};

export const leaveCallPresence = ({ immediate = false, keepalive = false } = {}) => {
  depth = Math.max(0, depth - 1);
  if (depth > 0) return;

  const go = () => {
    leaveTimer = null;
    if (depth > 0) return;
    stopBeat();
    persistStatus('AVAILABLE', { keepalive });
    recordCallLeave({ keepalive }).catch(() => {});
  };

  if (immediate) {
    if (leaveTimer) {
      window.clearTimeout(leaveTimer);
      leaveTimer = null;
    }
    go();
    return;
  }

  if (leaveTimer) window.clearTimeout(leaveTimer);
  leaveTimer = window.setTimeout(go, LEAVE_DEBOUNCE_MS);
};

export const forceLeaveCallPresence = () => {
  depth = 0;
  if (leaveTimer) {
    window.clearTimeout(leaveTimer);
    leaveTimer = null;
  }
  stopBeat();
  persistStatus('AVAILABLE', { keepalive: true });
  recordCallLeave({ keepalive: true }).catch(() => {});
  store.dispatch(updateUserStatus({ status: 'AVAILABLE', source: 'CALL' }));
};

/** Main chat window: if "In a call" is stuck after the call window closed, restore Available. */
export const startStaleCallPresenceWatch = () => {
  if (typeof window === 'undefined') return () => {};

  const check = async () => {
    if (onCallSurface()) return;
    try {
      if (window.workloopRealm?.isCallWindowOpen) {
        const open = await window.workloopRealm.isCallWindowOpen();
        if (open) return;
      }
    } catch {
      // ignore
    }
    const status = String(store.getState().auth?.user?.status || '').toUpperCase();
    if (status !== 'IN_A_CALL') return;
    let beat = 0;
    try {
      beat = Number(localStorage.getItem(HEARTBEAT_KEY) || 0);
    } catch {
      beat = 0;
    }
    if (beat && Date.now() - beat < STALE_MS) return;
    forceLeaveCallPresence();
  };

  const id = window.setInterval(check, 3000);
  check();
  return () => window.clearInterval(id);
};
